/**
 * Deterministic input-delay lockstep (AD-008), with no idea what a socket is.
 *
 * The contract with the caller is three calls: hand it the live stick once per
 * tick you want to produce (`advance`), hand it whatever the peer sent
 * (`acceptRemote`), and step your simulation with the release it gives back.
 * If it gives back `null`, the peer's stick for that tick has not arrived and
 * the simulation must **wait** — that is the whole mechanism, and the reason
 * this file contains no clock, no `Date.now`, and no interpolation.
 *
 * Why the local player's own input is delayed too: it is the only way both
 * peers step tick N with the identical pair of sticks. Reacting instantly to
 * your own stick and late to the peer's is the "client prediction" design, and
 * it needs rollback, a rewindable simulation and a resync protocol — none of
 * which v1 has.
 *
 * The delay window is pre-loaded with neutral inputs on both sides. Nothing is
 * transmitted to establish it: both peers derive the same prefill from the
 * agreed `inputDelay`, so the first `inputDelay` ticks fly hands-off while the
 * first real filings are in the air.
 */

import { NEUTRAL_INPUTS, type FlightInputs } from "@red-opus/engine";
import { InputLedger } from "./input-ledger.ts";
import { canonicalInputs, decodeInputs, encodeInputs, type EncodedInputs } from "./protocol.ts";

/** ≈67 ms at 60 Hz — enough to cover a typical intra-continental round trip. */
export const DEFAULT_INPUT_DELAY = 4;

/** Ticks between encounter-hash exchanges. One second of fight per integrity check. */
export const DEFAULT_HASH_INTERVAL = 60;

/**
 * How many past filings ride along in every input frame. A DataChannel opened
 * reliable/ordered will not drop any, but the cost of carrying eight is about
 * 40 bytes and it makes the protocol survive an unreliable channel unchanged.
 */
export const INPUT_REDUNDANCY = 8;

/** Ticks past the local one a remote filing may claim before it is discarded as junk. */
export const MAX_REMOTE_LEAD = 600;

export interface LockstepConfig {
  readonly localId: string;
  readonly remoteId: string;
  readonly inputDelay?: number;
  readonly hashInterval?: number;
  readonly redundancy?: number;
}

/** A run of consecutive filings to transmit, oldest first. */
export interface OutgoingInputs {
  readonly tick: number;
  readonly runs: readonly EncodedInputs[];
}

export interface TickRelease {
  readonly tick: number;
  readonly inputs: Readonly<Record<string, FlightInputs>>;
  /** True when this tick lands on the hash-exchange schedule. */
  readonly hashDue: boolean;
}

export interface LockstepAdvance {
  /** What to transmit, or `null` when this call produced nothing new to say. */
  readonly send: OutgoingInputs | null;
  /** The tick to simulate, or `null` when the peer's stick has not arrived. */
  readonly release: TickRelease | null;
}

/**
 * The opening hash.
 *
 * Everything that makes two peers build *different encounters* — a different
 * build's tunables, a setting read from the wrong side, a `DuelSetup` field one
 * of them decoded differently — is already wrong at construction and therefore
 * shows on the very first stepped tick. Measured against the real runners, a
 * pair whose setups disagreed diverged on tick 1 and was told at tick 60: a
 * whole second of flying a fight that existed on one screen only, and a second
 * is what "instantly" means to a player. Checking tick 1 as well as the regular
 * schedule costs one extra `encounterHash` per duel and turns that second into
 * 17 ms.
 *
 * It does not replace the interval — a divergence that develops mid-fight is a
 * different failure and still wants the periodic check.
 */
export const OPENING_HASH_TICK = 1;

/** True when `tick` lands on the hash-exchange schedule. `interval <= 0` disables it. */
export function shouldHash(tick: number, interval: number): boolean {
  if (interval <= 0) return false;
  return tick === OPENING_HASH_TICK || (tick > 0 && tick % interval === 0);
}

export class Lockstep {
  readonly localId: string;
  readonly remoteId: string;
  readonly inputDelay: number;
  readonly hashInterval: number;

  private readonly redundancy: number;
  private readonly ledger: InputLedger;
  private readonly recent: EncodedInputs[] = [];
  private recentFirst = 0;
  private simTick = 0;
  private filedThrough: number;
  private stalls = 0;

  constructor(config: LockstepConfig) {
    this.localId = config.localId;
    this.remoteId = config.remoteId;
    this.inputDelay = config.inputDelay ?? DEFAULT_INPUT_DELAY;
    this.hashInterval = config.hashInterval ?? DEFAULT_HASH_INTERVAL;
    this.redundancy = config.redundancy ?? INPUT_REDUNDANCY;
    this.ledger = new InputLedger([config.localId, config.remoteId]);
    this.filedThrough = this.inputDelay;
    this.prefill();
  }

  /** Both peers derive the same hands-off opening from `inputDelay` alone. */
  private prefill(): void {
    const neutral = canonicalInputs(NEUTRAL_INPUTS);
    for (let tick = 1; tick <= this.inputDelay; tick++) {
      this.ledger.record(this.localId, tick, neutral);
      this.ledger.record(this.remoteId, tick, neutral);
    }
  }

  /** Ticks simulated so far. The next release, if any, is `tick + 1`. */
  get tick(): number {
    return this.simTick;
  }

  /** Consecutive `advance` calls that produced no release — what a "waiting for opponent" banner counts. */
  get stalledTicks(): number {
    return this.stalls;
  }

  /** How far ahead of the simulation the peer's filings reach. Healthy is around `inputDelay`. */
  get remoteLead(): number {
    return Math.max(0, this.ledger.highest(this.remoteId) - this.simTick);
  }

  /** Who the next tick is waiting on. Empty when it is ready to go. */
  waitingOn(): readonly string[] {
    return this.ledger.missing(this.simTick + 1);
  }

  private window(): OutgoingInputs | null {
    if (this.recent.length === 0) return null;
    return { tick: this.recentFirst, runs: [...this.recent] };
  }

  private fileLocal(live: FlightInputs): boolean {
    const target = this.simTick + 1 + this.inputDelay;
    if (target <= this.filedThrough) return false;
    this.ledger.record(this.localId, target, canonicalInputs(live));
    this.recent.push(encodeInputs(live));
    if (this.recent.length === 1) this.recentFirst = target;
    while (this.recent.length > this.redundancy) {
      this.recent.shift();
      this.recentFirst += 1;
    }
    this.filedThrough = target;
    return true;
  }

  private tryRelease(): TickRelease | null {
    const next = this.simTick + 1;
    if (!this.ledger.ready(next)) {
      this.stalls += 1;
      return null;
    }
    const inputs = this.ledger.take(next);
    this.simTick = next;
    this.stalls = 0;
    this.ledger.prune(next);
    return { tick: next, inputs, hashDue: shouldHash(next, this.hashInterval) };
  }

  /**
   * One attempt at one tick. Files the local stick for `tick + 1 + inputDelay`
   * exactly once, then releases the next tick if the peer's stick for it has
   * landed. While stalled it re-offers the redundancy window, so a duel
   * recovers from lost frames without a retransmit protocol.
   */
  advance(live: FlightInputs): LockstepAdvance {
    const filed = this.fileLocal(live);
    const release = this.tryRelease();
    return { send: filed || release === null ? this.window() : null, release };
  }

  /** Books a run of the peer's filings. Already-simulated and absurdly-far-ahead ticks are dropped. */
  acceptRemote(firstTick: number, runs: readonly EncodedInputs[]): void {
    for (const [index, encoded] of runs.entries()) {
      const tick = firstTick + index;
      if (tick <= this.simTick || tick > this.simTick + MAX_REMOTE_LEAD) continue;
      this.ledger.record(this.remoteId, tick, decodeInputs(encoded));
    }
  }
}
